import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useCRMAuth } from '@/contexts/CRMAuthContext';
import CRMLayout from './CRMLayout';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, CheckCircle2, Clock, Target, TrendingUp,
  TrendingDown, BarChart3, User, Briefcase, Calendar
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function CRMPerformance() {
  const { userId } = useParams();
  const { token } = useCRMAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    fetchPerformance();
  }, [userId, period]);

  const fetchPerformance = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/crm/performance/${userId}?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setData(await res.json());
      } else {
        toast.error('Failed to load performance data');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <CRMLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </CRMLayout>
    );
  }

  if (!data) {
    return (
      <CRMLayout>
        <div className="text-center py-20">
          <p className="text-slate-500">Performance data not found</p>
          <Link to="/crm/team"><Button className="mt-4">Back to Team</Button></Link>
        </div>
      </CRMLayout>
    );
  }

  const { user, stats, comparison, daily_chart, work_breakdown } = data;
  const maxItems = Math.max(...daily_chart.map(d => d.items), 1);

  return (
    <CRMLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Link to="/crm/team">
            <Button variant="ghost" size="icon"><ArrowLeft className="w-5 h-5" /></Button>
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white text-xl font-bold">
              {user.name?.charAt(0)}
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{user.name}</h1>
              <p className="text-slate-500 text-sm">{user.designation || user.role}</p>
            </div>
          </div>
        </div>
        
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[140px]">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
            <SelectItem value="quarter">This Quarter</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 sm:p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Target className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs sm:text-sm text-slate-500">Total Tasks</p>
              <p className="text-xl sm:text-2xl font-bold text-slate-900">{stats.total_tasks}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 sm:p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs sm:text-sm text-slate-500">Completed</p>
              <p className="text-xl sm:text-2xl font-bold text-emerald-600">{stats.completed_tasks}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 sm:p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs sm:text-sm text-slate-500">Items Delivered</p>
              <p className="text-xl sm:text-2xl font-bold text-purple-600">{stats.total_items_delivered}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 sm:p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs sm:text-sm text-slate-500">Completion Rate</p>
              <p className="text-xl sm:text-2xl font-bold text-amber-600">{stats.completion_rate}%</p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Team Comparison */}
        <Card className="p-5">
          <h3 className="font-semibold text-slate-900 mb-4">vs Team Average</h3>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-slate-600">Items Delivered</span>
                <span className={`text-sm font-medium flex items-center gap-1 ${comparison.items_vs_avg >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {comparison.items_vs_avg >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {comparison.items_vs_avg >= 0 ? '+' : ''}{comparison.items_vs_avg}%
                </span>
              </div>
              <p className="text-xs text-slate-500">Team avg: {comparison.team_avg_items} items</p>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-slate-600">Tasks Completed</span>
                <span className={`text-sm font-medium flex items-center gap-1 ${comparison.tasks_vs_avg >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {comparison.tasks_vs_avg >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {comparison.tasks_vs_avg >= 0 ? '+' : ''}{comparison.tasks_vs_avg}%
                </span>
              </div>
              <p className="text-xs text-slate-500">Team avg: {comparison.team_avg_tasks} tasks</p>
            </div>
          </div>
        </Card>

        {/* Daily Chart */}
        <Card className="p-5 lg:col-span-2">
          <h3 className="font-semibold text-slate-900 mb-4">Daily Output (Last 14 Days)</h3>
          <div className="flex items-end gap-1 h-32">
            {daily_chart.map((day, idx) => (
              <div key={idx} className="flex-1 flex flex-col items-center group">
                <div 
                  className="w-full bg-indigo-500 rounded-t hover:bg-indigo-600 transition-colors cursor-pointer"
                  style={{ height: `${(day.items / maxItems) * 100}%`, minHeight: day.items > 0 ? '4px' : '0' }}
                  title={`${day.date}: ${day.items} items`}
                />
                <span className="text-[10px] text-slate-400 mt-1 hidden sm:block">
                  {new Date(day.date).getDate()}
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* Work Breakdown */}
        <Card className="p-5 lg:col-span-3">
          <h3 className="font-semibold text-slate-900 mb-4">Work Breakdown</h3>
          {work_breakdown.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No work logged in this period</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {work_breakdown.map((item, idx) => (
                <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-900">{item.name}</p>
                  <p className="text-2xl font-bold text-indigo-600">{item.quantity}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </CRMLayout>
  );
}
